<template>
  <div class="min-h-screen bg-white flex flex-col">
    <TrustBar />
    <main class="flex-1 pt-[120px] pb-12">
      <div class="max-w-4xl mx-auto px-4">
        <h1 class="text-3xl font-bold text-gray-900 mb-6">Conditions Générales d'Utilisation</h1>

        <div class="prose prose-gray max-w-none">
          <section class="mb-8">
            <p class="text-gray-600 mb-4">
              <strong>Dernière mise à jour :</strong> Janvier 2025
            </p>
            <p class="text-gray-600 mb-4">
              Les présentes Conditions Générales d'Utilisation (CGU) régissent l'utilisation du site Fluxreload, exploité par Fluxbuy OÜ (code d'immatriculation : 17222874).
            </p>
            <p class="text-gray-600">
              En accédant à ce site et en utilisant nos services, vous acceptez les présentes conditions. Si vous n'acceptez pas ces conditions, veuillez ne pas utiliser notre site.
            </p>
          </section>

          <section class="mb-8">
            <h2 class="text-2xl font-semibold text-gray-900 mb-4">1. Description du service</h2>
            <p class="text-gray-600 mb-4">Fluxreload est une plateforme de vente en ligne de produits numériques proposant :</p>
            <ul class="list-disc list-inside text-gray-600 space-y-2 mb-4">
              <li><strong>Cartes cadeaux</strong> : pour les boutiques en ligne internationales (shopping, gaming, streaming, voyages)</li>
              <li><strong>Cartes de paiement virtuelles</strong> : cartes PCS et autres cartes prépayées pour les achats en ligne</li>
              <li><strong>Recharges mobiles</strong> : crédit téléphonique, forfaits internet et bundles pour de multiples opérateurs</li>
              <li><strong>eSIM</strong> : cartes SIM virtuelles pour les voyages internationaux</li>
            </ul>
            <p class="text-gray-600">
              Les produits sont fournis instantanément sous forme de codes numériques envoyés par email.
            </p>
          </section>

          <section class="mb-8">
            <h2 class="text-2xl font-semibold text-gray-900 mb-4">2. Eligibilité et inscription</h2>
            <p class="text-gray-600 mb-4">Pour utiliser nos services, vous devez :</p>
            <ul class="list-disc list-inside text-gray-600 space-y-2">
              <li>Avoir la capacité légale de conclure des contrats (être majeur selon la loi de votre juridiction)</li>
              <li>Fournir des informations exactes, complètes et à jour lors de votre inscription</li>
              <li>Respecter les lois et réglementations applicables dans votre pays de résidence</li>
            </ul>
          </section>

          <section class="mb-8">
            <h2 class="text-2xl font-semibold text-gray-900 mb-4">3. Commandes et paiement</h2>

            <h3 class="text-xl font-semibold text-gray-800 mb-2">3.1. Processus de commande</h3>
            <p class="text-gray-600 mb-4">Vous sélectionnez les produits, entrez votre adresse email et procédez au paiement via nos prestataires sécurisés. Une fois le paiement confirmé, le code vous est envoyé instantanément par email.</p>

            <h3 class="text-xl font-semibold text-gray-800 mb-2">3.2. Prix et frais</h3>
            <p class="text-gray-600 mb-4">
              Les prix affichés sur nos produits ne comprennent pas les frais de service. Un frais de service de 2% est ajouté au moment du paiement, avant validation définitive de votre commande. Le montant total (prix du produit + frais de service) vous est clairement indiqué dans le résumé de commande avant paiement.
            </p>
            <p class="text-gray-600 mb-4">
              Les prix sont présentés dans plusieurs devises (FCFA/XOF, USD, EUR, etc.) avec conversion automatique basée sur les taux de change en vigueur.
            </p>
            <p class="text-gray-600">
              Nous nous réservons le droit de modifier nos prix et frais de service à tout moment sans préavis. Le prix applicable est celui en vigueur au moment de la commande.
            </p>

            <h3 class="text-xl font-semibold text-gray-800 mb-2">3.3. Moyens de paiement</h3>
            <p class="text-gray-600">Nous acceptons les paiements par carte bancaire (via Stripe) et par compte PayPal. Tous les paiements sont sécurisés et conformes aux normes PCI-DSS.</p>
          </section>

          <section class="mb-8">
            <h2 class="text-2xl font-semibold text-gray-900 mb-4">4. Livraison et politique de remboursement</h2>
            <p class="text-gray-600 mb-4">
              Les codes sont livrés instantanément par email après confirmation du paiement. En raison de la nature numérique des produits vendus :
            </p>
            <ul class="list-disc list-inside text-gray-600 space-y-2">
              <li>Une fois le code envoyé par email, la vente est définitive</li>
              <li>Les codes ne peuvent pas être échangés ni remboursés, sauf en cas de code défectueux ou non fonctionnel</li>
              <li>En cas de code défectueux, nous nous engageons à le remplacer par un code fonctionnel sous 48h</li>
            </ul>
            <p class="text-gray-600 mt-4">
              Il est de votre responsabilité de vérifier que le produit acheté est compatible avec le service destinataire (pays, opérateur, plateforme).
            </p>
          </section>

          <section class="mb-8">
            <h2 class="text-2xl font-semibold text-gray-900 mb-4">5. Responsabilités de l'utilisateur</h2>
            <p class="text-gray-600 mb-4">En utilisant notre service, vous vous engagez à :</p>
            <ul class="list-disc list-inside text-gray-600 space-y-2">
              <li>Fournir des informations exactes et véridiques</li>
              <li>Ne pas utiliser les codes à des fins illégales ou frauduleuses</li>
              <li>Protéger vos identifiants de connexion et mots de passe</li>
              <li>Ne pas tenter de contourner les mesures de sécurité du site</li>
              <li>Respecter les conditions d'utilisation des tiers (boutiques en ligne, opérateurs mobiles)</li>
            </ul>
          </section>

          <section class="mb-8">
            <h2 class="text-2xl font-semibold text-gray-900 mb-4">6. Propriété intellectuelle</h2>
            <p class="text-gray-600 mb-4">
              Tous les éléments du site Fluxreload (textes, images, logos, design, structure, marques) sont la propriété exclusive de Fluxbuy OÜ ou de ses partenaires et sont protégés par les lois sur la propriété intellectuelle.
            </p>
            <p class="text-gray-600">
              Toute reproduction, modification, distribution ou représentation, totale ou partielle, du site ou de son contenu est interdite sans autorisation préalable écrite.
            </p>
          </section>

          <section class="mb-8">
            <h2 class="text-2xl font-semibold text-gray-900 mb-4">7. Protection des données personnelles</h2>
            <p class="text-gray-600">
              Vos données personnelles sont traitées conformément à notre Politique de Confidentialité. Nous nous engageons à protéger vos informations et à ne pas les partager avec des tiers sans votre consentement, sauf obligation légale ou nécessité technique pour la fourniture du service.
            </p>
          </section>

          <section class="mb-8">
            <h2 class="text-2xl font-semibold text-gray-900 mb-4">8. Limitation de responsabilité</h2>
            <p class="text-gray-600 mb-4">Fluxbuy OÜ s'efforce d'assurer la disponibilité et la qualité du service mais ne peut garantir :</p>
            <ul class="list-disc list-inside text-gray-600 space-y-2 mb-4">
              <li>L'accès ininterrompu ou exempt d'erreurs au site</li>
              <li>La compatibilité des codes avec tous les services tiers</li>
              <li>La disponibilité permanente des produits auprès de nos fournisseurs</li>
            </ul>
            <p class="text-gray-600">
              Fluxbuy OÜ ne peut être tenu responsable des dommages directs ou indirects résultant de l'utilisation ou de l'impossibilité d'utiliser nos services, y compris les pertes de données ou de profits.
            </p>
          </section>

          <section class="mb-8">
            <h2 class="text-2xl font-semibold text-gray-900 mb-4">9. Force majeure</h2>
            <p class="text-gray-600">
              Fluxbuy OÜ ne sera pas tenu responsable des manquements ou retards dans l'exécution de ses obligations en cas de force majeure (grèves, conflits, catastrophes naturelles, perturbations des réseaux de télécommunication, etc.).
            </p>
          </section>

          <section class="mb-8">
            <h2 class="text-2xl font-semibold text-gray-900 mb-4">10. Modification des conditions</h2>
            <p class="text-gray-600">
              Nous nous réservons le droit de modifier ces conditions à tout moment. Les modifications prendront effet dès leur mise en ligne sur le site. Votre utilisation continue du service après les modifications constitue une acceptation des nouvelles conditions.
            </p>
          </section>

          <section class="mb-8">
            <h2 class="text-2xl font-semibold text-gray-900 mb-4">11. Loi applicable et juridiction</h2>
            <p class="text-gray-600">
              Les présentes conditions sont régies par la loi en vigueur au Bénin. Tout litige relatif à l'utilisation du site sera soumis aux juridictions compétentes du Bénin.
            </p>
          </section>

          <section>
            <h2 class="text-2xl font-semibold text-gray-900 mb-4">12. Contact</h2>
            <p class="text-gray-600">
              Pour toute question concernant ces conditions, contactez-nous via notre formulaire de contact disponible sur le site.
            </p>
          </section>
        </div>
      </div>
    </main>
  </div>
</template>

<script setup>
import { ref } from 'vue';
</script>

<style scoped>
.prose {
  color: #374151;
  line-height: 1.75;
}

.prose h2 {
  margin-top: 2em;
  margin-bottom: 1em;
  line-height: 1.25;
}

.prose p {
  margin-bottom: 1.25em;
}
</style>
